import React, { useState } from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import styled, { css } from 'styled-components';
import { lighten } from 'polished';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var colors = {
  // Core
  darkCore: '#ff5e00',
  core: '#fe8c00',
  sunlight: '#fff496',
  // Sale
  rouge: '#ff5a52',
  // Label
  warmBlack: '#323220',
  grass: '#158620',
  // Primary colors
  wave: '#3d6778',
  lightTeal: '#b0ccca',
  silver: '#d6e2e5',
  olive: '#70763d',
  lightOlive: '#c0cba2',
  hazel: '#8f5000',
  sand: '#eee1d2',
  // UI colors
  black: '#000',
  almostblack: '#202020',
  darkGray: '#454545',
  darkGrayL1: '#707070',
  mediumGray: '#adadad',
  mediumGrayL1: '#c7c7c7',
  lightGray: '#e3e3e3',
  lightGrayL1: '#f7f7f7',
  white: '#fff',
  lightSand: '#f9f5f1',
  lightSandBox: '#fdf8cf',
  ocean: '#359cbb',
  oceanL1: '#60b9d4',
  morello: '#e42a25',
  morelloL1: '#e74b3f',
  spreadShadow: '#dfdfdf',
  // Globetrotter
  darkGlobe: '#172b54',
  darkGlobeL1: '#3a4b6d',
  globe: '#7594b3',
  globeL1: '#89a4be',
  latte: '#a48c6a',
  lightRouge: '#fabaac',
  mora: '#4c1029',
  moraL1: '#673448',
  // Spies
  mango: '#f6b637',
  // Other
  tripadvisor: '#589442',
  // Validation error
  validationErrorBg: '#facac7',
  validationErrorText: '#8f271f',
  validationErrorBorder: '#fd9e99',
  // Validation
  validationValid: '#1d9886'
};

var fonts = {
  bold: "'ThomasHeadlineBold', 'Arial Black'",
  "default": "'Source Sans Pro', 'Arial'",
  regular: "'ThomasHeadlineRegular', 'Arial'",
  wave: "'NLTGWaveSerif', 'Arial'"
};

var deviceWidths = {
  smallMobile: '320px',
  mobile: '375px',
  largeMobile: '414px',
  xLargeMobile: '435px',
  mobileLandscape: '480px',
  mobileLandscapeMax: '479px',
  largeMobileLandscape: '568px',
  largeMobileLandscapeMax: '567px',
  tablet: '768px',
  tabletMax: '767px',
  desktop: '975px',
  desktopMax: '974px',
  tabletLandscape: '1024px',
  tabletLandscapeMax: '1023px',
  mediumDesktop: '1280px',
  mediumDesktopMax: '1278px',
  largeDesktop: '1680px',
  largeDesktopMax: '1679px',
  xLargeDesktop: '1920px'
};

var transitionSpeeds = {
  "default": '.25s',
  smooth: '.5s',
  slow: '1s'
};
var transitions = {
  "default": "".concat(transitionSpeeds["default"], " ease"),
  smooth: "".concat(transitionSpeeds.smooth, " ease"),
  slow: "".concat(transitionSpeeds.slow, " ease")
};

var TabNav = styled.nav.withConfig({
  displayName: "style__TabNav",
  componentId: "sc-ppgsl6-0"
})(["border-radius:6px;display:flex;@media (max-width:", "){box-shadow:0 2px 4px ", ";}@media (min-width:", "){position:static;}"], deviceWidths.tabletMax, colors.mediumGrayL1, deviceWidths.tablet);
var TabNavButton = styled.button.withConfig({
  displayName: "style__TabNavButton",
  componentId: "sc-ppgsl6-1"
})(["background:", ";border:", " solid;border-width:1px 0 1px 1px;color:", ";display:block;flex-grow:1;font-family:", ";font-size:14px;line-height:18px;margin-bottom:-1px;min-height:40px;padding:4px 8px;-webkit-tap-highlight-color:transparent;text-align:center;text-transform:uppercase;transition:background ", ",color ", ";@media (max-width:", "){max-width:50%;", "}@media (min-width:", "){flex-grow:0;flex-shrink:1;min-width:180px;max-width:240px;padding:8px;}&:first-of-type{border-bottom-left-radius:6px;border-top-left-radius:6px;}&:last-of-type{border-bottom-right-radius:6px;border-width:1px;border-top-right-radius:6px;}", " ", " ", ""], colors.white, colors.mediumGrayL1, colors.darkGray, fonts.bold, transitions["default"], transitions["default"], deviceWidths.tabletMax, function (props) {
  return props.isMultiple && css(["  font-size:12px;line-height:14px;padding:8px 0;"]);
}, deviceWidths.tablet, function (props) {
  return props.isSelected && css(["background:", ";border-color:", ";color:", ";position:relative;&::after{border-color:", " transparent transparent;border-style:solid;border-width:8px 8px 0;content:'';height:0;left:50%;position:absolute;top:100%;transform:translate(-50%,calc(-100% + 8px));width:0;}"], colors.wave, colors.wave, colors.white, colors.wave);
}, function (props) {
  return !props.isDisabled && !props.isSelected && css([".no-touch &:hover{background:", ";border-color:", ";color:", ";}"], lighten(0.1, colors.wave), lighten(0.1, colors.wave), colors.white);
}, function (props) {
  return props.isDisabled && css(["background:", ";color:", " pointer-events:none;position:relative;"], colors.lightGray, colors.lightGrayL1);
});

var TabNav$1 = function TabNav$1(_ref) {
  var className = _ref.className,
      tabs = _ref.tabs,
      selected = _ref.selected,
      _onClick = _ref.onClick;

  if (!tabs || !tabs.length) {
    return null;
  }

  var _useState = useState(selected),
      _useState2 = _slicedToArray(_useState, 2),
      selectedTab = _useState2[0],
      setSelectedTab = _useState2[1];

  return React.createElement(TabNav, {
    className: className,
    isMultiple: tabs.length > 2
  }, tabs.filter(function (t) {
    return t;
  }).map(function (tab, i) {
    return React.createElement(TabNavButton, _extends({
      key: uniqid('tab-nav-button-'),
      onClick: function onClick() {
        if (!tab.disabled) {
          _onClick(i);

          setSelectedTab(i);
        }
      },
      type: "button",
      isDisabled: tab.disabled,
      isSelected: selectedTab === i
    }, tab.attribute ? _defineProperty({}, tab.attribute.name, tab.attribute.value) : {}), tab.value ? React.createElement("span", {
      dangerouslySetInnerHTML: {
        __html: tab.value
      }
    }) : tab);
  }));
};

TabNav$1.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool,
    attribute: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  })])),
  className: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.number
};
TabNav$1.defaultProps = {
  className: null,
  tabs: [],
  onClick: function onClick() {},
  selected: 0
};

export { TabNav$1 as TabNav };
//# sourceMappingURL=index.js.map
